/**
 * Módulo del tutorial: Tacto y preñez (KAN-232).
 *
 * Sesión REAL de tacto, como se trabaja en el campo: Mi Ganado → Nueva
 * Sesión → tildar "Preñez (Tacto)" y "Tiene Cría" en el arranque (no vienen
 * por defecto) → cargar una vaca: caravana, categoría VACA, la Fecha de
 * Evento como fecha del tacto, el resultado (Vacia / P1 / P2 / P3 —
 * trimestres de gestación) y el ternero al pie → guardar → salir → ver la
 * sesión con el filtro "Con tacto", la ficha del animal y el reporte de
 * preñez. Incluye la vigencia de 8 meses y el caso VACÍA + ternero = parió.
 *
 * Anclas: menu-animals, animals-btn-nueva-sesion, field-selector-prenies,
 * field-selector-start, form-animal-caravana/categoria/fecha-evento/prenez/
 * ternero, btn-save-animal, animal-session-salir, sesiones-filtro-tacto,
 * animals-row-ver, animal-detalle-prenez, reports-nav-pregnancy.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { sesionAbiertaConAnimales } from '../../../services/tutorial/sessionCondition';
import { getRepositories } from '../../../services/storage';

export const tactoModule: TutorialModuleDef = {
  id: 'tacto',
  title: 'Tacto: el dato del año',
  description: 'Cargá una sesión de tacto real — preñez con su fecha y ternero al pie — y mirá cómo se arma sola la tasa de preñez del rodeo.',
  emoji: '🩺',
  image: 'images/tutorial/tacto.png',
  permission: { module: 'animals', action: 'create' },
  grupo: 'esencial',
  route: '/animals',
  steps: [
    {
      id: 'concepto-tacto',
      route: '/tutorial',
      text: 'Un service flojo se paga caro: el tacto es el dato que decide el año. Vamos a cargar una sesión de tacto REAL, como en la manga — y el reporte de preñez se arma solo.',
      narration:
        'Hablemos del dato que decide si el año cierra redondo: el tacto. Saber cuántas vacas vienen preñadas es saber cuántos terneros van a nacer, y eso es el negocio entero. Y lo vamos a hacer como en la vida real: una sesión de tacto, vaca por vaca, con su resultado y su fecha. Cuando termines, el porcentaje de preñez del rodeo se arma solo. Empecemos.',
    },
    {
      id: 'ir-a-mi-ganado-tacto',
      target: '[data-ar-guide="menu-animals"]',
      label: 'Mi Ganado (menú Ganado)',
      text: 'Los tactos se cargan por sesión, igual que los pesajes: andá a "Mi Ganado" para arrancarla.',
      narration:
        'El tacto se carga por sesión, igual que el pesaje: una jornada de manga, una sesión. Andá al menú, sección Ganado, y tocá Mi Ganado: ahí arrancamos.',
    },
    {
      id: 'nueva-sesion-tacto',
      route: '/animals',
      target: '[data-ar-guide="animals-btn-nueva-sesion"]',
      label: 'Nueva Sesión',
      text: 'Tocá "Nueva Sesión" — ésta va a ser la jornada de tacto.',
      narration:
        'Tocá Nueva Sesión y abrimos la jornada del tacto.',
    },
    {
      // Paso condicional: sólo aparece si el usuario ya tenía una sesión de
      // carga abierta con animales (las sesiones quedan abiertas hasta que
      // se inicia una nueva — SessionContext persiste en localStorage).
      id: 'sesion-abierta-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="sesion-modal-nueva"]',
      label: 'Cerrá la sesión anterior',
      showIf: sesionAbiertaConAnimales,
      text: '¡Ojo! Tenés una sesión ABIERTA de la última vez (las sesiones quedan abiertas hasta que iniciás una nueva). Tocá "Nueva Sesión" acá para cerrar esa y arrancar una limpia para el tacto.',
      narration:
        'Un momento, que me suena a vieja conocida: tenés una sesión abierta de la última vez. Tranquilo, no se perdió nada, es que las sesiones de esta app quedan abiertas hasta que vos iniciás una nueva, aunque cierres el navegador o pases días. Tocá Nueva Sesión, el botón colorado de este aviso: esa sesión vieja queda guardada con su fecha, y arrancamos una limpia para el tacto.',
    },
    {
      id: 'selector-prenez-ternero',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-prenies"]',
      extraTargets: ['[data-ar-guide="field-selector-tiene_ternero"]'],
      label: 'Tildá "Preñez (Tacto)" y "Tiene Cría"',
      advanceOnClick: false,
      text: 'OJO: estos dos campos NO vienen marcados por defecto. Tildá "Preñez (Tacto)" y también "Tiene Cría": es para las hembras con cría EN PIE — ya parieron, y esa cría cuenta para la preñez del año. Sin tildarlos, los campos no aparecen.',
      narration:
        'Parate un segundo acá, porque éste es el tropezo clásico: estos dos campos no vienen marcados por defecto, ni en la carga rápida. Tildá vos mismo Preñez, Tacto, ése de acá. Y el otro, Tiene Cría, también: ése es para las hembras que ya parieron y tienen la cría al pie. No lo saltees, porque esa cría cuenta para la preñez del año: una vaca vacía pero con ternero, parió, y la app se lo reconoce. Sin esos dos tildes, el formulario no te va a preguntar nada de eso.',
    },
    {
      id: 'comenzar-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="field-selector-start"]',
      label: 'Comenzar a cargar',
      text: 'Con "Preñez (Tacto)" y "Tiene Cría" marcadas, tocá "Comenzar a cargar".',
      narration:
        'Ya con los dos campos marcados, apretá Comenzar a cargar, y a la manga.',
    },
    {
      id: 'vaca-caravana',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-caravana"]',
      advanceOnClick: false,
      label: 'Caravana de la vaca',
      text: 'Cargá la caravana de una VACA tuya — la primera de la fila de la manga. Si ya está en el sistema, la reconoce y le suma el tacto a su historia.',
      narration:
        'Vamos vaca por vaca, como en la fila de la manga. Cargá la caravana de la primera. Si la vaca ya está en el sistema, la app la reconoce al toque: este tacto se suma a su historia sin pisar nada de lo anterior.',
    },
    {
      id: 'vaca-categoria',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-categoria"]',
      advanceOnClick: false,
      label: 'Categoría: Vaca',
      text: 'Categoría "Vaca": el campo de Estado de Preñez sólo aparece para vacas (las vaquillonas se tactan igual, pero el campo de cría les queda directo).',
      narration:
        'La categoría: Vaca. Fijate que al elegirla aparece abajo el campo de Estado de Preñez: ése campo es de vacas. Si en tu campo también tactás vaquillonas, a ellas les queda directo el campo de la cría, y el resto funciona igual.',
    },
    {
      id: 'fecha-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-fecha-evento"]',
      advanceOnClick: false,
      label: 'Fecha de Evento = fecha del tacto',
      text: 'La "Fecha de Evento" es LA FECHA DEL TACTO — no la de hoy si lo hiciste otro día. Con esa fecha la app cuenta los meses de preñez y corre el reloj de los 8 meses.',
      narration:
        'Y la Fecha de Evento, atendé a ésto: es la fecha en que se hizo el tacto de verdad, no la de hoy por default. Si tacteaste el jueves y cargás el sábado, poné el jueves. Con esa fecha la app sabe cuántos meses de preñez lleva la vaca, y cuándo hay que volver a preguntarle.',
    },
    {
      id: 'estado-prenez',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-prenez"]',
      advanceOnClick: false,
      label: 'Estado de Preñez',
      text: 'El resultado del tacto: "Vacia", o P1 / P2 / P3 — los trimestres de gestación (P1: hasta 3 meses, P3: ya está por parir). Si tenía un tacto viejo, lo ves de referencia: no se pierde nunca.',
      narration:
        'El momento de la verdad: el resultado del tacto. Vacia, si vino vacía. O el trimestre de preñez: P uno, hasta tres meses; P dos, de tres a seis; P tres, ya está por parir. Si la vaca ya tenía un tacto viejo, lo vas a ver ahí mismo de referencia, y si pasó del plazo, la app te lo avisa y arranca preguntando el nuevo. El historial nunca se pierde: cada tacto queda con su fecha.',
    },
    {
      id: 'tiene-ternero-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="form-animal-ternero"]',
      advanceOnClick: false,
      label: 'Tiene Ternero',
      text: '¿Tiene ternero al pie? Marcálo acá: una vaca VACÍA CON ternero PARÓ — y a esa la app la cuenta A FAVOR en tu tasa de preñez.',
      narration:
        'Y el detalle que pocos saben: ¿la vaca tiene ternero al pie? Marcáselo acá. Porque una vaca que tactó vacía pero tiene el ternero al pie, no es un fracaso: parió. Y la app la cuenta a favor en la tasa de preñez, no en contra. Datos bien cargados, números que defienden al campo.',
    },
    {
      id: 'guardar-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="btn-save-animal"]',
      label: 'Guardar',
      text: 'Guardá — el tacto quedó registrado con su fecha. Y seguís con la próxima vaca, sin salir de la sesión.',
      narration:
        'Guardá, y listo: el tacto quedó anotado con su fecha. Y el formulario se limpia solo para que cargues la próxima vaca de la fila, sin salir de la sesión. Así toda la tarde de manga, vaca por vaca.',
    },
    {
      id: 'salir-sesion-tacto',
      route: '/animals/new',
      target: '[data-ar-guide="animal-session-salir"]',
      label: 'Salir (cerrar la jornada)',
      text: 'Jornada terminada: tocá "Salir" para cerrar la sesión de tacto.',
      narration:
        'Cuando terminaste la fila, cerrá la jornada con Salir. La sesión queda guardada con su fecha y sus vacas, como la página del libro de campo.',
    },
    {
      id: 'sesion-con-tacto',
      route: '/sesiones',
      target: '[data-ar-guide="sesiones-filtro-tacto"]',
      label: 'Filtro "Con tacto"',
      advanceOnClick: false,
      text: 'Tu sesión de tacto ya está en la lista. Y con el filtro "Con tacto" ves sólo las sesiones donde se tactearon animales — útil para encontrar la del año pasado.',
      narration:
        '¿La ves? Ahí está tu sesión de tacto, con su fecha. Y fijate en estos filtros: Con tacto te muestra sólo las jornadas donde se cargaron tactos. Cuando quieras comparar con el service del año pasado, un toque y la encontrás.',
    },
    {
      id: 'ver-en-ficha-tacto',
      route: '/animals',
      target: '[data-ar-guide="animals-row-ver"]',
      label: 'El ojito de tu vaca',
      text: 'Abrí la ficha de tu vaca con el OJITO de su fila — quiero que veas dónde quedó el dato.',
      narration:
        'Ahora busquemos a la vaca en su casa: en Mi Ganado, tocá el ojito de su fila y se abre la ficha completa.',
    },
    {
      id: 'ficha-prenez-tacto',
      target: '[data-ar-guide="animal-detalle-prenez"]',
      advanceOnClick: false,
      label: 'Preñez en la ficha',
      text: 'Acá vive todo: "Estado de Preñez" con su fecha, "Tiene Ternero", y la "Fertilidad Histórica" de la vaca — su historia reproductiva completa. Pasá el cursor y ves cada tacto.',
      narration:
        'Mirá qué prolijito quedó: el estado de preñez con su fecha, el ternero al pie si lo tiene, y más abajo la fertilidad histórica de la vaca, año por año. Si le pasás el cursor arriba del estado, te cuenta la historia completa, tacto por tacto. Ésta es la libreta que nunca se te pierde.',
    },
    {
      id: 'reporte-prenez-cierre',
      route: '/reports',
      target: '[data-ar-guide="reports-nav-pregnancy"]',
      extraTargets: ['[data-ar-guide="qa-reports"]'],
      label: 'Análisis de Preñez (Reportes)',
      advanceOnClick: false,
      text: 'Y el retorno del dato: en Reportes, el "Análisis de Preñez" — la tasa del rodeo calculada sola, con la evolución de los últimos años. A Reportes volvés con "Reporte del Campo", adentro de la categoría Ganado del menú (te lo marco).',
      narration:
        'Y ahora sí, el premio: en Reportes, el Análisis de Preñez. La tasa de tu rodeo, calculada sola con los tactos que cargaste, y al lado la evolución de los últimos años para ver si el service viene mejorando. Y para volver acá cuando quieras, es el botón Reporte del Campo, en el menú sección Ganado, ese que te estoy marcando. Cargás el tacto una vez, y el dato te rinde todo el año. Ése es el tacto bien aprovechado, criollo.',
    },
  ],
  completionCheck: async () => {
    // Alguna hembra con registro de preñez o ternero al pie.
    const animales = await getRepositories().animal.getAllForActiveEmpresa();
    return animales.some(
      a => a.prenies != null || a.tiene_ternero === true,
    );
  },
};
